import { useCallback, useEffect, useRef, useState } from "react";
import useLiveResource from "../../../hooks/useLiveResource";
import { readUserNotifications, markNotificationRead, markAllNotificationsRead, saveNotificationPreferences } from "../services/notifications";
import { connectNotificationTabs } from "../services/notificationTabs";

const empty = { items: [], unread_items: [], unread: 0, as_of: "" };

/** Account scope is supplied by the inbox's keyed parent. No booking mutations. */
export default function useNotificationInbox(writable, merchantId, accountId) {
  const [filter, setFilter] = useState("all");
  const [saving, setSaving] = useState("");
  const [saveError, setSaveError] = useState("");
  const [allRetry, setAllRetry] = useState("");
  const [preferenceRetry, setPreferenceRetry] = useState(null);
  const [preferenceError, setPreferenceError] = useState("");
  const [preferenceSaved, setPreferenceSaved] = useState(false);
  const [toastsPaused, setToastsPaused] = useState(false);
  const active = useRef(true);
  const pending = useRef(null);
  const channel = useRef(null);
  const invalidated = useRef(false);
  const loader = useCallback(async signal => {
    const value = await readUserNotifications(signal);
    if ((value?.merchant_id !== undefined && value.merchant_id !== merchantId)
        || (value?.account_id !== undefined && value.account_id !== accountId)) {
      throw Object.assign(new Error("Notification account changed. Check your session before retrying."), { response: { status: 403 } });
    }
    if (!Array.isArray(value?.items) || !Number.isInteger(value.unread) || value.unread < 0
        || (value.unread_items !== undefined && !Array.isArray(value.unread_items))
        || (value.preferences !== undefined && typeof value.preferences?.down_payment_extension_toasts !== "boolean")) {
      throw new Error("Notifications could not be loaded.");
    }
    return { ...value, unread_items: value.unread_items ?? value.items.filter(row => !row.read_at) };
  }, [merchantId, accountId]);
  const resource = useLiveResource(loader, empty, true, { financeTenant: merchantId });
  const latestResource = useRef(resource); latestResource.current = resource;
  const syncRead = useCallback(() => {
    const latest = latestResource.current;
    if (!active.current || latest.loading || latest.error) return;
    invalidated.current = false;
    latest.refresh({ supersede: true });
  }, []);
  useEffect(() => {
    active.current = true;
    return () => { active.current = false; pending.current?.abort(); };
  }, []);
  useEffect(() => {
    if (!merchantId || !accountId) return undefined;
    const connection = connectNotificationTabs(merchantId, accountId, () => { invalidated.current = true; syncRead(); });
    channel.current = connection;
    return () => { connection.close(); if (channel.current === connection) channel.current = null; };
  }, [merchantId, accountId, syncRead]);
  useEffect(() => { if (invalidated.current) syncRead(); }, [resource.loading, resource.error, syncRead]);
  useEffect(() => {
    window.addEventListener("background-jobs-change", syncRead);
    return () => window.removeEventListener("background-jobs-change", syncRead);
  }, [syncRead]);
  const markRead = async id => {
    if (pending.current || !active.current || !writable || resource.loading || resource.error) return false;
    const all = !id;
    // An uncertain bulk retry must keep its original cutoff even if a live read
    // has since returned newer notices. Already-read timestamps stay unchanged.
    const through = allRetry || resource.data.as_of;
    if (all && !through) return false;
    const controller = new AbortController(); pending.current = controller;
    setSaving(all ? "all" : id); setSaveError("");
    if (all) setAllRetry(through);
    try {
      const result = all ? await markAllNotificationsRead(through, controller.signal) : await markNotificationRead(id, controller.signal);
      if (result?.ok !== true) throw new Error("Notification read status was not confirmed.");
      if (active.current && !controller.signal.aborted) {
        if (all) setAllRetry("");
        channel.current?.publish();
        const refreshed = await resource.refresh({ supersede: true });
        return !!refreshed && active.current && !controller.signal.aborted;
      }
    } catch (error) {
      if (active.current && !controller.signal.aborted) {
        if ([401, 403, 404].includes(error?.response?.status)) await resource.reload();
        else setSaveError(all ? "Could not confirm all notifications as read. Retry uses the same cutoff; newer notices stay unread."
          : "Could not mark the notification as read. Please retry.");
      }
    } finally {
      if (pending.current === controller) pending.current = null;
      if (active.current) setSaving("");
    }
    return false;
  };
  const viewNotification = async (notice, onOpen) => {
    if (pending.current || !active.current || resource.loading || resource.error) return false;
    // Existing read-only module policy still permits opening authorized details.
    // A late acknowledgement from an old account must never navigate the new one.
    if (notice.read_at || !writable || await markRead(notice.id)) {
      if (active.current) { onOpen(); return true; }
    }
    return false;
  };
  const saveToasts = async enabled => {
    if (typeof enabled !== "boolean" || pending.current || !active.current || !writable || resource.loading || resource.error) return false;
    const controller = new AbortController(); pending.current = controller;
    setSaving("preferences"); setPreferenceError(""); setPreferenceSaved(false);
    setPreferenceRetry(enabled); setToastsPaused(true);
    try {
      const result = await saveNotificationPreferences(enabled, controller.signal);
      if (result?.ok !== true || result.preferences?.down_payment_extension_toasts !== enabled) throw new Error("Unconfirmed preference");
      if (active.current && !controller.signal.aborted) {
        channel.current?.publish("PREFERENCES_CHANGED");
        const refreshed = await resource.refresh({ supersede: true });
        if (!active.current || controller.signal.aborted) return false;
        if (!refreshed) throw new Error("Unconfirmed inbox refresh");
        setToastsPaused(false); setPreferenceRetry(null); setPreferenceSaved(true);
        return true;
      }
    } catch (error) {
      if (active.current && !controller.signal.aborted) {
        if ([401, 403, 404].includes(error?.response?.status)) await resource.reload();
        setPreferenceError("Could not confirm the toast preference. Toasts are paused in this tab until you retry. Inbox notifications remain available.");
      }
    } finally {
      if (pending.current === controller) pending.current = null;
      if (active.current) setSaving("");
    }
    return false;
  };
  const items = filter === "unread" ? resource.data.unread_items : resource.data.items;
  return { resource: { ...resource, toastsPaused }, filter, setFilter, items, saving, saveError, allRetry, markRead, viewNotification,
    saveToasts, preferenceRetry, preferenceError, preferenceSaved };
}
